export type WatermarkPosition = 'top-right' | 'bottom-right' | 'bottom-left' | 'top-left';

export async function addWatermark(imageUrl: string, text: string, position: WatermarkPosition) {
  const blob = await addWatermarkBlob(imageUrl, text, position);
  const downloadUrl = URL.createObjectURL(blob);

  return downloadUrl;
}

export async function addWatermarkToFile(image: File, text: string, position: WatermarkPosition) {
  const imageUrl = URL.createObjectURL(image);

  try {
    const blob = await addWatermarkBlob(imageUrl, text, position);
    const fileName = image.name.replace(/\.[^.]+$/, '') || 'image';

    return new File([blob], `${fileName}.png`, {
      type: blob.type,
      lastModified: image.lastModified,
    });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

async function addWatermarkBlob(imageUrl: string, text: string, position: WatermarkPosition) {
  const image = await loadImage(imageUrl);

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Could not create a canvas context');
  }

  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;

  context.drawImage(image, 0, 0);
  drawTextWatermark(context, canvas, text, position);

  const blob = await canvasToBlob(canvas, 'image/png');

  return blob;
}

function drawTextWatermark(
  context: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  text: string,
  position: WatermarkPosition
) {
  const marginX = Math.round(canvas.width * 0.025);
  const marginY = Math.round(canvas.height * 0.025);
  const fontSize = Math.max(10, Math.round(canvas.width * 0.01));
  const lineHeight = fontSize * 1.25;
  const [vertical, horizontal] = position.split('-') as ['top' | 'bottom', 'left' | 'right'];
  const lines = text.split('\n');

  context.save();

  context.font = `400 ${fontSize}px Inter, sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'bottom';

  const maxWidth = Math.max(...lines.map((line) => context.measureText(line).width));
  const padding = fontSize * 0.3;

  // Position the text block in the requested corner.
  // With textAlign=center and textBaseline=bottom, the anchor is the
  // center-bottom of each line.
  const x = horizontal === 'right' ? canvas.width - marginX - maxWidth / 2 : marginX + maxWidth / 2;

  const yStart =
    vertical === 'top'
      ? marginY + lineHeight
      : canvas.height - marginY - (lines.length - 1) * lineHeight;

  // Light translucent background behind the whole text block.
  const bgWidth = maxWidth + padding * 2;
  const bgHeight = lines.length * lineHeight + padding * 2;
  const bgX = x - bgWidth / 2;
  const bgY = yStart - lineHeight - padding;

  context.fillStyle = 'rgba(255, 255, 255, 0.5)';
  context.shadowColor = 'transparent';
  context.shadowBlur = 0;
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.fillRect(bgX, bgY, bgWidth, bgHeight);

  // Dark text
  context.fillStyle = 'rgba(0, 0, 0, 0.7)';
  context.shadowColor = 'rgba(0, 0, 0, 0.1)';
  context.shadowBlur = 2;
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 1;

  lines.forEach((line, i) => {
    context.fillText(line, x, yStart + i * lineHeight);
  });

  context.restore();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Could not load image: ${src}`));
    image.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Could not create image blob'));
      }
    }, type);
  });
}
